import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/authContext";
import { Helmet } from "react-helmet";
import Map, { Marker, FullscreenControl, Popup } from 'react-map-gl/mapbox';
import supercluster from 'supercluster';
import ChangeMap from "../../components/changeMap/changeMap";
import NavigationRoundedIcon from '@mui/icons-material/NavigationRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import NearMeDisabledRoundedIcon from '@mui/icons-material/NearMeDisabledRounded';
import TimelineIcon from '@mui/icons-material/Timeline';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import axiosInstance from "../../api/axiosInstance";
import moment from "moment";
import 'maplibre-gl/dist/maplibre-gl.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import "./mapScreen.scss";

const MapScreen = () => {
    const [locations, setLocations] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [search, setSearch] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [selected, setSelected] = useState(null);
    const [popupSelected, setPopupSelected] = useState({});
    const [bottomSheet, setBottomSheet] = useState(false);
    const [mapSettings, setMapSettings] = useState(() => {
        try {
            const cached = localStorage.getItem("mapSettings");
            return cached ? JSON.parse(cached) : { style: "mapbox://styles/mapbox/traffic-day-v2" };
        } catch (err) {
            console.error("Invalid mapSettings in localStorage, resetting:", err);
            return { style: "mapbox://styles/mapbox/traffic-day-v2" };
        }
    });
    const [category, setCategory] = useState(() => {
        try {
            const cached = localStorage.getItem("mapCategories");
            return cached ? JSON.parse(cached) : [];
        } catch (err) {
            console.error("Invalid mapCategories in localStorage, resetting:", err);
            return [];
        }
    });
    const [viewState, setViewState] = useState({
        latitude: 46.7795,
        longitude: 11.8816,
        zoom: 13,
    });

    const mapRef = useRef(null);
    const location = useLocation();

    const touchStartY = useRef(null);

    const { hasPermission } = useContext(AuthContext);

    const fetchData = useCallback(async () => {
        try {
            const [locationsRes, objectsRes] = await Promise.all([
                axiosInstance.get(`map`, { params: { category } }),
                axiosInstance.get('ergo/constructionsites'),
            ]);
            setLocations(locationsRes.data);

            setCombinedData([
                ...locationsRes.data,
                ...(category.includes(0) ? objectsRes.data : [])
            ]);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [category]);

    useEffect(() => {
        fetchData();

        const interval = setInterval(() => {
            fetchData();
        }, 10000);

        return () => clearInterval(interval);
    }, [fetchData]);


    // Aktuelle Position
    useEffect(() => {
        let watchId;

        if (navigator.geolocation) {
            watchId = navigator.geolocation.watchPosition(
                (position) => {
                    setUserLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                },
                (error) => {
                    console.error("Geolocation error:", error);
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 10000,
                    timeout: 5000,
                }
            );
        }

        return () => {
            if (watchId) navigator.geolocation.clearWatch(watchId);
        };
    }, []);

    // Konvertiere Locations in GeoJSON Features für supercluster
    const points = useMemo(() => combinedData.map(location => ({
        type: "Feature",
        properties: {
            cluster: false,
            id: location.id,
            itemAlias: location.itemAlias || "B" + location.id,
            itemKennzeichen: location.itemKennzeichen || location.objectAlias,
            itemDescription: location.itemDescription || location.objectCompanyAlias,
            itemDriverAlias: location.itemDriverAlias,
            itemProjectManager: location.objectProjectManager,
            ignition: location.ignition,
            itemCategory: location.itemCategory,
            angle: location.angle,
            fuelLevel: location.fuelLevel,
            fuelLevelLt: location.fuelLevelLt,
            totalMileage: location.totalMileage,
            hours: location.hours,
            recivedAt: location.recivedAt,
        },
        geometry: {
            type: "Point",
            coordinates: [location.longitude, location.latitude],
        }
    })), [combinedData]);

    // Erstelle supercluster Instance
    const cluster = useMemo(() => new supercluster({
        radius: 75,
        maxZoom: 20,
    }).load(points), [points]);

    // Clusterpunkte für aktuellen Zoom und Viewport holen, nur wenn Karte geladen ist
    const clusters = useMemo(() => {
        if (!mapRef.current || !mapLoaded) return [];

        const map = mapRef.current.getMap();

        if (!map || !map.getBounds) return [];

        const bounds = map.getBounds().toArray().flat();

        if (!bounds || bounds.length === 0) return [];

        return cluster.getClusters(bounds, Math.round(viewState.zoom));
    }, [cluster, viewState, mapLoaded]);

    // Popup öffnen / schließen
    const togglePopup = ({ id, longitude, latitude, props, fly = false }) => {
        // Gleiches Popup wieder klicken => schließen
        if (popupSelected?.id === id) {
            setPopupSelected({});
            return;
        }

        // Neues Popup öffnen
        setPopupSelected({
            id,
            coordinates: [longitude, latitude],
            props,
        });

        // Optional Karte fokussieren
        if (fly) {
            flyToLocation({
                coordinates: [longitude, latitude],
                properties: props,
            });
        }
    };

    // Klick auf Marker: zoomt rein
    const flyToLocation = async ({ coordinates, properties }) => {
        if (!mapRef.current) return;

        const map = mapRef.current.getMap();
        const bounds = map.getBounds().toArray().flat();
        const MAX_ZOOM = 20;

        let zoom = Math.round(viewState.zoom);
        let foundCluster;

        // Solange der Punkt Teil eines Clusters ist, erhöhe Zoom
        while (zoom < MAX_ZOOM) {
            const clustersAtZoom = cluster.getClusters(bounds, zoom);
            foundCluster = clustersAtZoom.find(c => {
                if (!c.properties.cluster) return false;
                const leaves = cluster.getLeaves(c.id, Infinity);
                return leaves.some(child => child.properties.id === properties.id);
            });

            if (!foundCluster) break; // Punkt ist nicht mehr geclustert
            zoom += 1;
        }

        // Immer noch ein Schritt weiter zoomen (falls möglich)
        const finalZoom = Math.min(zoom + 2, MAX_ZOOM);

        map.flyTo({
            center: coordinates,
            zoom: finalZoom,
            speed: 1.2,
        });

        setSelected(properties.id || {});

        setBottomSheet(false);
    };

    // Klick auf Cluster: zoomt rein
    const onClusterClick = (clusterId) => {
        const clusterPoint = clusters.find(c => c.id === clusterId);
        if (!clusterPoint) return;
    };

    // Zu aktuelle Position springen
    const flyToUserLocation = () => {
        if (!mapRef.current || !userLocation) return;

        const map = mapRef.current.getMap();

        map.flyTo({
            center: [userLocation.longitude, userLocation.latitude],
            zoom: 16,
            speed: 1.2,
            essential: true,
        });
    };

    // Liste durchsuchen
    const filteredLocations = useMemo(() => {
        if (!searchText) return locations;

        const query = searchText.toLowerCase();

        return locations.filter(item => {
            return (
                item.itemAlias?.toLowerCase().includes(query) ||
                item.itemDescription?.toLowerCase().includes(query) ||
                item.itemDriverAlias?.toLowerCase().includes(query) ||
                item.itemKennzeichen?.toLowerCase().includes(query)
            );
        });
    }, [locations, searchText]);

    // Suche umschalten
    const toggleSearch = () => {
        setSearch(prev => !prev);
        setSearchText("")
    };

    // Bottom Sheet Swipe-Handler
    const handleTouchStart = (e) => {
        touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
        if (touchStartY.current === null) return;

        const touchEndY = e.changedTouches[0].clientY;
        const diff = touchStartY.current - touchEndY;

        // Nach oben wischen
        if (diff > 50) {
            setBottomSheet(true);
        }

        // Nach unten wischen
        if (diff < -50) {
            setBottomSheet(false);
        }

        touchStartY.current = null;
    };

    return isLoading ? (<div className="loading" />) : (
        <div className={location.pathname === "/mapWarter" ? "map eckart" : "map"}>
            <Helmet>
                <title>Karte</title>
            </Helmet>
            <div className="left">
                <div className="content">
                    <Map
                        ref={mapRef}
                        initialViewState={viewState}
                        mapStyle={mapSettings.style}
                        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                        style={{ width: '100%', height: '100%' }}
                        onMove={evt => setViewState(evt.viewState)}
                        bearing={0}
                        maxPitch={0}
                        onLoad={() => setMapLoaded(true)}
                        onClick={() => setPopupSelected({})}
                    >
                        <FullscreenControl position="top-right" />
                        <ChangeMap
                            mapSettings={mapSettings}
                            setMapSettings={setMapSettings}
                            category={category}
                            setCategory={setCategory}
                            flyToUserLocation={flyToUserLocation}
                            isLocation={userLocation}
                        />
                        <Marker
                            longitude={userLocation?.longitude || 0}
                            latitude={userLocation?.latitude || 0}
                        >
                            <div className="user-location-marker" hidden={!userLocation}>
                                <div className="dot" />
                            </div>
                        </Marker>
                        {clusters.map(clusterPoint => {
                            const [longitude, latitude] = clusterPoint.geometry.coordinates;
                            const { cluster: isCluster, point_count: pointCount, id, ...props } = clusterPoint.properties;

                            if (isCluster) {
                                // Cluster Marker
                                return (
                                    <Marker
                                        key={`cluster-${clusterPoint.id}`}
                                        longitude={longitude}
                                        latitude={latitude}
                                        onClick={() => onClusterClick(clusterPoint.id)}
                                    >
                                        <div
                                            className="cluster-marker"
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                const leaves = cluster.getLeaves(clusterPoint.id, Infinity);

                                                setPopupSelected({
                                                    id: clusterPoint.id,
                                                    type: "cluster",
                                                    coordinates: [longitude, latitude],
                                                    items: leaves.map(l => l.properties)
                                                });

                                                onClusterClick(clusterPoint.id);
                                            }}
                                        >
                                            {pointCount}
                                        </div>
                                        {popupSelected?.id === clusterPoint.id &&
                                            <Popup
                                                longitude={longitude}
                                                latitude={latitude}
                                                offset={15}
                                                onClose={() => setPopupSelected({})}
                                                closeOnClick={false}
                                            >
                                                <div className="custom-popup">
                                                    <div className="top">
                                                        <LocalShippingOutlinedIcon className="off" />
                                                        <div className="textRight">
                                                            <span className="title">
                                                                {pointCount} Fahrzeuge
                                                            </span>
                                                            <span className="description">Cluster</span>
                                                        </div>
                                                    </div>
                                                    <div className="line" />
                                                    <div className="list">
                                                        {popupSelected.items.map((item, index) => (
                                                            <div
                                                                className="group"
                                                                key={index}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();

                                                                    togglePopup({
                                                                        id,
                                                                        longitude,
                                                                        latitude,
                                                                        props,
                                                                        fly: true,
                                                                    });
                                                                }}
                                                            >
                                                                <text className={item.ignition ? "on" : "off"}>{item.itemAlias}</text>
                                                                <div className="textRight">
                                                                    <span>{item.itemKennzeichen}</span>
                                                                    {hasPermission("timeline", "read") &&
                                                                        <Link className={item.ignition ? "timeline go" : "timeline stop"} to={`timeline/${item.id}`} title="Zeitachse" >
                                                                            <TimelineIcon />
                                                                        </Link>
                                                                    }
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Popup>
                                        }
                                    </Marker>
                                );
                            }

                            // Einzelmarker
                            return (
                                <Marker
                                    key={id}
                                    longitude={longitude}
                                    latitude={latitude}
                                >
                                    <div
                                        className="mark"
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            togglePopup({
                                                id,
                                                longitude,
                                                latitude,
                                                props,
                                                fly: true,
                                            });
                                        }}
                                    >
                                        {props.itemCategory === 100
                                            ? <div className="arrow construction">
                                                <LocationOnRoundedIcon />
                                            </div>
                                            : <div className={props.ignition ? "arrow on" : "arrow off"}>
                                                <NavigationRoundedIcon style={{ transform: `rotate(${props.angle}deg)` }} />
                                            </div>
                                        }
                                        <div className="badge">
                                            <span className="nr">{props.itemAlias}</span>
                                        </div>
                                    </div>
                                    {popupSelected.id === id &&
                                        <Popup
                                            longitude={longitude}
                                            latitude={latitude}
                                            offset={15}
                                            onClose={() => setPopupSelected({})}
                                            closeOnClick={false}
                                        >
                                            <div className="custom-popup">
                                                <div className="top">
                                                    <LocalShippingOutlinedIcon className={props.ignition === 1 ? "on" : "off"} />
                                                    <div className="textRight">
                                                        <span
                                                            className="title"
                                                            title={props.itemAlias + " - " + props.itemKennzeichen}
                                                        >
                                                            {props.itemAlias} - {props.itemKennzeichen}
                                                        </span>
                                                        <span className="description">{props.itemDescription}</span>
                                                    </div>
                                                </div>
                                                <div className="line" />
                                                <div className="list">
                                                    {props.itemDriverAlias && (
                                                        <div className="group" title="Fahrer">
                                                            <ManageAccountsOutlinedIcon />
                                                            <div className="textRight">
                                                                <span>{props.itemDriverAlias}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {props.fuelLevel && (
                                                        <div className="group" title="Tankinhalt">
                                                            <LocalGasStationOutlinedIcon />
                                                            <div className="textRight">
                                                                <span>{props.fuelLevel} %</span>
                                                                <div className="chart">
                                                                    <div
                                                                        className="bar"
                                                                        style={{
                                                                            width: `${props.fuelLevel}%`,
                                                                            background:
                                                                                props.fuelLevel <= 30
                                                                                    ? "#cd071e"
                                                                                    : props.fuelLevel <= 60
                                                                                        ? "#ff8c00"
                                                                                        : "#03b73c"
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {props.fuelLevelLt && !props.fuelLevel && (
                                                        <div className="group" title="Tankinhalt">
                                                            <LocalGasStationOutlinedIcon />
                                                            <div className="textRight">
                                                                <span>{props.fuelLevelLt} l</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {props.totalMileage && (
                                                        <div className="group" title="Kilometerstand">
                                                            <RouteOutlinedIcon />
                                                            <div className="textRight">
                                                                <span>{props.totalMileage.toLocaleString('de-DE')} km</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {props.hours && (
                                                        <div className="group" title="Stunden">
                                                            <HourglassBottomRoundedIcon />
                                                            <div className="textRight">
                                                                <span>{props.hours.toLocaleString('de-DE')} h</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {props.itemProjectManager && (
                                                        <div className="group" title="Projektleiter">
                                                            <ManageAccountsOutlinedIcon />
                                                            <div className="textRight">
                                                                <span>{props.itemProjectManager}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <Link className="group" title="In Google Maps öffnen" to={`https://www.google.com/maps/place/${latitude},${longitude}`} target="blank">
                                                        <LocationPinIcon />
                                                        <div className="textRight">
                                                            <span>Google Maps</span>
                                                            <OpenInNewRoundedIcon />
                                                        </div>
                                                    </Link>
                                                    {hasPermission("timeline", "read") && props.itemCategory !== 100 &&
                                                        <Link className="group" to={`timeline/${id}`} title="Zeitachse" >
                                                            <TimelineIcon className="timeline" />
                                                            <div className="textRight">
                                                                <span>Zeitachse</span>
                                                                <ArrowForwardIosRoundedIcon />
                                                            </div>
                                                        </Link>
                                                    }
                                                </div>
                                                <div className="line" />
                                                <div className="bottom">
                                                    <div className={props.ignition === 1 ? "dot on" : "dot off"}></div>
                                                    <span className="lastChange">{moment(props.recivedAt).fromNow()}</span>
                                                </div>
                                            </div>
                                        </Popup>
                                    }
                                </Marker>
                            );
                        })}
                    </Map>
                </div>
            </div>
            {location.pathname !== "/mapWarter" &&
                <div
                    className={bottomSheet ? "vehicleList open" : "vehicleList"}
                >
                    <div
                        className="handle"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="bar" />
                    </div>
                    <div className="content">
                        <div className="scroll">
                            <div className="top">
                                {search ?
                                    <div className="left">
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Suche"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </div>
                                    :
                                    <div className="left">
                                        <h2>Fahrzeuge</h2>
                                        <span>{locations.length}</span>
                                    </div>
                                }
                                <div className="right">
                                    <SearchRoundedIcon onClick={toggleSearch} />
                                </div>
                            </div>
                            <div className="list">
                                {filteredLocations.map(item => (
                                    <div
                                        className={selected === item.id ? "item active" : "item"}
                                        key={item.id}
                                        onClick={() => flyToLocation({ coordinates: [item.longitude, item.latitude], properties: item })}
                                    >
                                        <div className={item.ignition ? "badge go" : "badge stop"}>
                                            <NavigationRoundedIcon />
                                        </div>
                                        <div className="data">
                                            <span className="alias">{item.itemAlias} - {item.itemDescription}</span>
                                            <span className="driver">{item.itemDriverAlias}</span>
                                            <div className="bottom">
                                                <div className="infoBox">
                                                    {item.itemKennzeichen &&
                                                        <div className="kennzeichen">
                                                            <span>{item.itemKennzeichen}</span>
                                                        </div>
                                                    }
                                                    <div className="timestamp">
                                                        <span>{moment(item.recivedAt).fromNow()}</span>
                                                    </div>
                                                </div>
                                                {hasPermission("timeline", "read") &&
                                                    <Link className={item.ignition ? "timeline go" : "timeline stop"} to={`timeline/${item.id}`} title="Zeitachse" >
                                                        <TimelineIcon />
                                                    </Link>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {locations.length === 0 &&
                            <div className="empty">
                                <NearMeDisabledRoundedIcon />
                                <span>Keine Tracker in dieser Kategorie</span>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    );
};

export default MapScreen;